// ItemActionMenu — per-item regen entry point embedded in content views.
//
// 19BJ Session 2: Regen embedded architecture migration.
// Design doc: docs/architecture/regen-embedded-design.md §2.1.
//
// Renders a ⟳ icon button alongside FlagButton (🚩). Clicking opens a
// dropdown menu with regen actions (replace / add / delete / hint).
// Selecting an action triggers useUnifiedRegen and shows RegenPreview.
//
// UX boundary (Audit B1.3):
//   - FlagButton: "This content is wrong" → mark + wait for repair
//   - ItemActionMenu: "I want more/different content" → active regen

import { useState, useCallback, useRef, useEffect } from 'react';
import { RefreshCw, Plus, Trash2, MessageSquarePlus } from 'lucide-react';
import { useUnifiedRegen } from '../lib/hooks/use-unified-regen.js';
import { useStore } from '../lib/store/index.js';
import { useT } from '../i18n/index.js';
import { RegenPreview } from './regen-preview.js';
import type { RegenAction } from '@examforge/shared/schemas';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ItemActionMenuProps {
  /** Course ID — required for regen API calls. */
  courseId: string;
  /** The item to operate on. id is required; caseIds/conceptIds scope the regen. */
  item: {
    id: string;
    caseIds?: string[];
    conceptIds?: string[];
  };
  /** Which stage this item belongs to (e.g. 'mcqs', 'flashcards'). */
  stage: string;
  /** Chapter ID for chapter-scoped regen. */
  chapterId?: string;
  /** Called after regen is accepted and merged. */
  onRegenComplete?: () => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Build a RegenAction from primitive values. Uses explicit type casting
 *  to avoid TS index-signature issues with Zod passthrough schemas. */
function makeRegenAction(
  kind: RegenAction['kind'],
  stage: string,
  itemId: string,
  itemConceptIds: string[] | undefined,
  chapterId: string | undefined,
  hint?: string,
): RegenAction {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scope: Record<string, any> = { stage };

  switch (kind) {
    case 'replace':
      scope['itemIds'] = [itemId];
      break;
    case 'add':
      if (itemConceptIds?.length) scope['conceptIds'] = itemConceptIds;
      else scope['caseIds'] = [chapterId ?? itemId];
      break;
    case 'delete':
      scope['itemIds'] = [itemId];
      break;
  }

  // Passthrough schemas accept extra keys — cast through unknown.
  if (kind === 'replace' && hint) {
    return { kind, scope, userFeedback: hint } as unknown as RegenAction;
  }
  if (kind === 'add') {
    return { kind, scope, count: 3 } as unknown as RegenAction;
  }
  return { kind, scope } as unknown as RegenAction;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function ItemActionMenu({
  item,
  stage,
  chapterId,
  courseId,
  onRegenComplete,
}: ItemActionMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showHintInput, setShowHintInput] = useState(false);
  const [hintText, setHintText] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const hintRef = useRef<HTMLTextAreaElement>(null);

  const regen = useUnifiedRegen(courseId);
  // Extract the two stable sub-references used by the poll bridge effect below
  // (regen.status etc. used elsewhere stay on the `regen` object).
  // (react-hooks/exhaustive-deps · Part E 第四波)
  const { path: regenPath, loadPreviewFromRegenSlice } = regen;
  const t = useT();

  // Close menu on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setShowHintInput(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Focus hint textarea when it appears.
  useEffect(() => {
    if (showHintInput && hintRef.current) {
      hintRef.current.focus();
    }
  }, [showHintInput]);

  // ── Action handlers ─────────────────────────────────────────────────────────

  const handleAction = useCallback(async (kind: RegenAction['kind']) => {
    setMenuOpen(false);
    setShowHintInput(false);

    const action = makeRegenAction(
      kind,
      stage,
      item.id,
      item.conceptIds,
      chapterId,
      kind === 'replace' && hintText.trim() ? hintText.trim() : undefined,
    );

    setHintText('');
    await regen.trigger(action, stage, chapterId);
  }, [regen, stage, chapterId, item.id, item.conceptIds, hintText]);

  const handleHintSubmit = useCallback(() => {
    if (!hintText.trim()) return;
    void handleAction('replace');
  }, [hintText, handleAction]);

  // ── Deep path polling bridge ──────────────────────────────────────────────
  //
  // When regen is on the Deep path, items arrive via regenSlice polling.
  // Watch the global store and load preview when confirm state is reached.

  const regenStatus = useStore((s) => s.regenStatus);
  const regenPendingItems = useStore((s) => s.regenPendingItems);
  const regenBeforeSnapshot = useStore((s) => s.regenBeforeSnapshot);
  const regenDiffSummary = useStore((s) => s.regenDiffSummary);
  const regenSliceId = useStore((s) => s.regenId);

  useEffect(() => {
    if (
      regenPath === 'deep' &&
      regenStatus === 'confirm' &&
      regenPendingItems &&
      regenSliceId
    ) {
      loadPreviewFromRegenSlice(
        regenPendingItems,
        regenBeforeSnapshot ?? [],
        regenDiffSummary ?? null,
        regenSliceId,
      );
    }
  }, [regenPath, regenStatus, regenPendingItems, regenBeforeSnapshot,
      regenDiffSummary, regenSliceId, loadPreviewFromRegenSlice]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const isActive = menuOpen || regen.status === 'loading' || regen.status === 'preview';

  return (
    <span className="item-action-menu-wrap" ref={menuRef}>
      <button
        ref={btnRef}
        type="button"
        className={`item-action-btn${isActive ? ' is-active' : ''}`}
        onClick={() => setMenuOpen((v) => !v)}
        title={t('itemAction.buttonLabel')}
        aria-label={t('itemAction.buttonLabel')}
        aria-expanded={menuOpen}
      >
        <RefreshCw size={11} aria-hidden="true" />
      </button>

      {menuOpen && (
        <div className="item-action-dropdown">
          {!showHintInput ? (
            <>
              <button
                type="button"
                className="item-action-menu-item"
                onClick={() => void handleAction('replace')}
              >
                <RefreshCw size={12} aria-hidden="true" />
                <span>{t('itemAction.replace')}</span>
              </button>
              <button
                type="button"
                className="item-action-menu-item"
                onClick={() => void handleAction('add')}
              >
                <Plus size={12} aria-hidden="true" />
                <span>{t('itemAction.addMore')}</span>
              </button>
              <button
                type="button"
                className="item-action-menu-item item-action-menu-item--danger"
                onClick={() => void handleAction('delete')}
              >
                <Trash2 size={12} aria-hidden="true" />
                <span>{t('itemAction.delete')}</span>
              </button>
              <button
                type="button"
                className="item-action-menu-item"
                onClick={() => setShowHintInput(true)}
              >
                <MessageSquarePlus size={12} aria-hidden="true" />
                <span>{t('itemAction.hint')}</span>
              </button>
            </>
          ) : (
            <div className="item-action-hint-wrap">
              <textarea
                ref={hintRef}
                className="item-action-hint-input"
                rows={3}
                placeholder={t('itemAction.hintPlaceholder')}
                value={hintText}
                onChange={(e) => setHintText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleHintSubmit();
                  }
                  if (e.key === 'Escape') {
                    setShowHintInput(false);
                    setHintText('');
                  }
                }}
              />
              <div className="item-action-hint-actions">
                <button
                  type="button"
                  className="item-action-menu-item item-action-menu-item--small"
                  onClick={() => { setShowHintInput(false); setHintText(''); }}
                >
                  {t('itemAction.cancel')}
                </button>
                <button
                  type="button"
                  className="item-action-menu-item item-action-menu-item--small item-action-menu-item--primary"
                  onClick={handleHintSubmit}
                  disabled={!hintText.trim()}
                >
                  {t('itemAction.generate')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* RegenPreview inline overlay — shown when regen returns preview items. */}
      {regen.status === 'preview' && regen.previewItems.length > 0 && (
        <RegenPreview
          newItems={regen.previewItems}
          originalItems={regen.originalItems}
          stage={regen.stage}
          action={regen.action}
          path={regen.path}
          cost={regen.cost}
          triggerRef={btnRef}
          onAccept={(selected) => {
            void regen.accept(selected).then(() => onRegenComplete?.());
          }}
          onReject={() => regen.reject()}
        />
      )}

      {/* Loading indicator */}
      {regen.status === 'loading' && (
        <span className="item-action-loading">
          <RefreshCw size={10} className="item-action-spinner" aria-hidden="true" />
        </span>
      )}
    </span>
  );
}
